import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds";

const LINKS = [
  { key: "house", label: "The House", to: "/the-house" },
  { key: "gallery", label: "Gallery", to: "/gallery" },
  { key: "island-guide", label: "Island Guide", to: "/island-guide" },
  { key: "location", label: "Location", to: "/location" },
  { key: "book", label: "Book", to: "/book" },
];

export const SiteNav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onSubPage = location.pathname !== "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On subpages we force scrolled style so the nav is readable on white pages.
  const isOpaque = scrolled || onSubPage || open;

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-700 ${
        isOpaque
          ? "bg-[hsl(var(--linen))]/85 backdrop-blur-2xl border-b border-[hsl(var(--border))]/70"
          : "bg-gradient-to-b from-black/30 to-transparent"
      }`}
    >
      <div className="container-editorial flex items-center justify-between h-16 md:h-[78px]">
        <Link
          to="/"
          data-testid={NAV.logo}
          className={`font-serif-logo text-[22px] md:text-[26px] tracking-[0.45em] leading-none ${
            isOpaque ? "text-[hsl(var(--ink))]" : "text-white"
          }`}
        >
          ATA
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <Link
              key={l.key}
              to={l.to}
              data-testid={NAV.link(l.key)}
              className={`group relative text-[10px] font-light tracking-[0.32em] uppercase transition-colors duration-500 ${
                isOpaque ? "text-[hsl(var(--ink))]/85 hover:text-[hsl(var(--ink))]" : "text-white/90 hover:text-white"
              }`}
            >
              {l.label}
              <span
                className={`pointer-events-none absolute left-0 right-0 -bottom-1.5 h-px scale-x-0 origin-left transition-transform duration-500 group-hover:scale-x-100 ${
                  isOpaque ? "bg-[hsl(var(--ink))]" : "bg-white"
                }`}
              />
            </Link>
          ))}
        </nav>

        <Link
          to="/book"
          data-testid={NAV.bookCta}
          className={`hidden md:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[10px] tracking-[0.32em] uppercase transition-all duration-500 ${
            isOpaque
              ? "bg-[hsl(var(--ink))] text-[hsl(var(--linen))] hover:bg-[hsl(28_12%_8%)]"
              : "border border-white/55 text-white hover:bg-white/10"
          }`}
        >
          Check Availability
        </Link>

        <button
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className={`md:hidden p-2 ${isOpaque ? "text-[hsl(var(--ink))]" : "text-white"}`}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[hsl(var(--linen))] border-t border-[hsl(var(--border))]">
          <div className="container-editorial py-6 flex flex-col gap-5">
            {LINKS.map((l) => (
              <Link
                key={l.key}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${NAV.link(l.key)}`}
                className="text-sm tracking-[0.28em] uppercase text-[hsl(var(--ink))]"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/book"
              onClick={() => setOpen(false)}
              className="btn-pill btn-dark text-xs self-start"
            >
              Check Availability
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};
